import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getReadiness, ListingReadiness } from '../../../../api/stays-setup-client';
import { StatusPill } from '../../../status-pill/status-pill';
import { CardTitle, CheckItem, CheckLabel, Checklist, Quiet, ReadyCard } from './setup-wizard.styled';

/**
 * "READY TO RECEIVE GUESTS" (Independent Stays plan ISR, ISR.6, R-ISR.6).
 *
 * Every item the listing still misses, each one tap from the wizard step that
 * fixes it. Shown on the listing until everything is done; then it says so in
 * one line and gets out of the way (`hideWhenReady`). The list is the server's
 * (GET /stays/listings/:id/readiness): the page never decides readiness.
 */
export const ReadinessChecklist: React.FC<{
  listingId: string;
  /** Only the host can fix things: others see the list without the links. */
  canFix: boolean;
  hideWhenReady?: boolean;
  /** A parent that already read it (the wizard) passes it in. */
  readiness?: ListingReadiness | null;
}> = ({ listingId, canFix, hideWhenReady = false, readiness }) => {
  const [own, setOwn] = useState<ListingReadiness | null>(null);
  const [failed, setFailed] = useState(false);
  const seq = useRef(0);
  const data = readiness !== undefined ? readiness : own;

  const load = useCallback(() => {
    const mine = ++seq.current;
    setFailed(false);
    getReadiness(listingId)
      .then(r => {
        if (mine === seq.current) setOwn(r);
      })
      .catch(() => {
        if (mine === seq.current) setFailed(true);
      });
  }, [listingId]);

  useEffect(() => {
    if (readiness === undefined) load();
  }, [load, readiness]);

  // A failed read is not a reason to block the listing page: say nothing.
  if (failed || !data) return null;
  if (data.ready && hideWhenReady) return null;

  const missing = data.items.filter(i => !i.done).length;

  return (
    <ReadyCard aria-labelledby={`ready-${listingId}`} data-readiness={data.ready ? 'ready' : 'missing'}>
      <CardTitle id={`ready-${listingId}`}>
        {data.ready ? 'Ready to receive guests' : 'Before your first guest'}
      </CardTitle>
      {data.ready ? (
        <Quiet>Everything a guest needs is in place.</Quiet>
      ) : (
        <Quiet>
          {missing === 1 ? '1 thing is' : `${missing} things are`} still missing. Each one opens the step that fixes it.
        </Quiet>
      )}
      <Checklist>
        {data.items.map(i => (
          <CheckItem key={i.key} data-item={i.key}>
            <CheckLabel>
              <StatusPill tone={i.done ? 'success' : 'warning'} dot>
                {i.done ? 'Done' : 'Missing'}
              </StatusPill>
              {i.label}
            </CheckLabel>
            {!i.done && canFix ? (
              <Link to={i.fix_path} aria-label={`Fix: ${i.label}`}>
                Fix this
              </Link>
            ) : null}
          </CheckItem>
        ))}
      </Checklist>
      {!data.ready && canFix ? <Link to={`/dashboard/stays/setup/${listingId}`}>Continue the setup</Link> : null}
    </ReadyCard>
  );
};
